import shortid from "shortid";
import BaseFieldGenerator from "./BaseFieldGenerator";
import Checkbox from "../../../../../lib/components/checkbox/Checkbox";
import InputText from "../../inputFields/InputText";
import GF from "../../../../../lib/utils/GF";
import InputDateTimePicker from "../../inputFields/InputDateTimePicker";
import InputUpload from "../../inputFields/InputUpload";
import InputWysiwyg from "../../inputFields/InputWysiwyg";
import InputColorPicker from "../../inputFields/InputColorPicker";
import React from "react";
import InputLink from "../../inputFields/InputLink";
import InputDropDown from "../../inputFields/dropdown/InputDropDown";
import MenuItem from "../../menu/MenuItem";

class InputFieldGenerator extends BaseFieldGenerator {

  static extractLink(value) {
    let text = '', href = '', linkInfo = {};
    try {
      linkInfo = JSON.parse(value);
    } catch (e) {
      if (GF.checkNotNull(value) && value !== '') {
        value = value.replace(/&quot;/gm, '"');
        value = value.replace(/\\\//gm, '/');
        linkInfo = JSON.parse(value);
      }
    }
    if (linkInfo !== null) {
      text = linkInfo.text;
      href = linkInfo.href;
    }
    return {text: text, href: href};
  }

  static detectLink(value) {
    let isLink = false;

    if (typeof value == 'string') {
      let regex = /^{&quot;href&quot;:.*&quot;http.*&quot;}$/;
      if (regex.test(value)) {
        isLink = true;
      }
      regex = /^{"href":.*,"text":.*"}$/;
      if (regex.test(value)) {
        isLink = true;
      }
    }
    return isLink;
  }

  constructor(onChange = null, inputLayout = 'inline', size = 'medium', onErrorChange = null) {
    super();
    if (onChange === null) {
      onChange = (e, id, value) => {
        // dummy
      }
    }
    this.onChange = onChange;
    this.inputLayout = inputLayout;
    this.size = size;
    this.onErrorChange = onErrorChange;
  }

  initBooleanField(id, fieldRef, fieldValue, fieldLabel, privilege, constrains) {
    let defaultValue;
    switch (typeof fieldValue) {
      case 'string':
        defaultValue = fieldValue.toLowerCase() === 'ja' || fieldValue.toLowerCase() === 'yes';
        break;
      default:
        defaultValue = fieldValue;
    }
    return <Checkbox
      _id={id}
      key={shortid()}
      ref={fieldRef}
      checked={defaultValue}
      constrains={constrains}
      labelElement={fieldLabel}
      onErrorChange={this.onErrorChange}
      onChange={(e, id, value) => this.onChange(e, id, value)}
      disabled={BaseFieldGenerator.generateState(privilege)}/>;
  }

  initIntegerField(id, fieldRef, fieldValue, fieldLabel, privilege, constrains) {
    return this.initTextField(id, fieldRef, fieldValue, fieldLabel, privilege, undefined, constrains);
  }

  initFloatField(id, fieldRef, fieldValue, fieldLabel, privilege, constrains) {
    return this.initTextField(id, fieldRef, fieldValue, fieldLabel, privilege, undefined, constrains);
  }

  initTextAreaField(id, fieldRef, fieldValue, fieldLabel, privilege, dataLength, constrains) {
    return <InputText
      multiline={true}
      size={this.size}
      _id={id} key={shortid()}
      ref={fieldRef}
      labelElement={fieldLabel}
      defaultValue={fieldValue}
      constrains={constrains}
      onErrorChange={this.onErrorChange}
      onChange={(e, id, value) => this.onChange(e, id, value)}
      disabled={BaseFieldGenerator.generateState(privilege)}
      maxRows={8}
      maxLength={dataLength}/>;
  }

  initPasswordField(id, fieldRef, fieldValue, fieldLabel, privilege, constrains) {
    return <InputText
      type='password'
      _id={id}
      size={this.size}
      ref={fieldRef}
      key={shortid()}
      labelElement={fieldLabel}
      defaultValue={fieldValue}
      constrains={constrains}
      onErrorChange={this.onErrorChange}
      onChange={(e, id, value) => this.onChange(e, id, value)}
      disabled={BaseFieldGenerator.generateState(privilege)}/>;
  }

  initHtmlField(id, fieldRef, fieldValue, fieldLabel, valueMap, privilege, constrains) {
    return <InputWysiwyg
      _id={id}
      ref={fieldRef}
      key={shortid()}
      labelElement={fieldLabel}
      defaultValue={fieldValue}
      constrains={constrains}
      onErrorChange={this.onErrorChange}
      onChange={(e, id, value) => this.onChange(e, id, value)}
      variablesItems={valueMap}
      disabled={BaseFieldGenerator.generateState(privilege)}/>;
  }

  initLinkField(id, fieldRef, fieldValue, fieldLabel, privilege, constrains) {
    return <InputLink
      _id={id}
      size={this.size}
      ref={fieldRef}
      key={shortid()}
      labelElement={fieldLabel}
      defaultValue={InputFieldGenerator.extractLink(fieldValue)}
      constrains={constrains}
      onErrorChange={this.onErrorChange}
      onChange={(e, id, value) => this.onChange(e, id, value)}
      disabled={BaseFieldGenerator.generateState(privilege)}/>;
  }

  initTextField(id, fieldRef, fieldValue, fieldLabel, privilege, maxLength, constrains) {
    return <InputText
      _id={id}
      ref={fieldRef}
      size={this.size}
      key={shortid()}
      labelElement={fieldLabel}
      defaultValue={fieldValue}
      constrains={constrains}
      onErrorChange={this.onErrorChange}
      onChange={(e, id, value) => this.onChange(e, id, value)}
      disabled={BaseFieldGenerator.generateState(privilege)}
      maxLength={GF.getValue(maxLength, 255)}/>;
  }

  initDateTimePickerField(id, fieldRef, fieldValue, fieldLabel, privilege, useTime, constrains) {
    return <InputDateTimePicker
      _id={id}
      ref={fieldRef}
      key={shortid()}
      defaultValue={fieldValue}
      size={this.size}
      labelElement={fieldLabel}
      constrains={constrains}
      onErrorChange={this.onErrorChange}
      onChange={(e, id, value) => this.onChange(e, id, value)}
      useTime={useTime}
      canChooseUseTime={true}
      disabled={BaseFieldGenerator.generateState(privilege)}/>;
  }

  initColorPickerField(id, fieldRef, fieldValue, fieldLabel, privilege, constrains) {
    return <InputColorPicker
      _id={id}
      ref={fieldRef}
      size={this.size}
      key={shortid()}
      hex={fieldValue}
      labelElement={fieldLabel}
      constrains={constrains}
      onErrorChange={this.onErrorChange}
      onChange={(e, id, value) => this.onChange(e, id, value)}
      disabled={BaseFieldGenerator.generateState(privilege)}/>
  }

  initEnumField(fieldType, id, fieldRef, fieldValue, fieldLabel, privilege, constrains) {
    let options = [];
    let defaultKeyValue = '';
    if (GF.checkNotNull(fieldType.valueMap)) {
      if (typeof fieldType.valueMap === 'object') {
        for (let index in fieldType.valueMap) {
          let enumValue = fieldType.valueMap[index];
          index = parseInt(index);
          if (typeof enumValue === 'object') {
            if (Array.isArray(enumValue)) {
              if (index === fieldValue) {
                defaultKeyValue = index;
              }
              // options.push({id: index, label: enumValue[0]});
              options.push(<MenuItem key={index} value={index}>{enumValue[0]}</MenuItem>);
            } else {
              if (enumValue.key === fieldValue) {
                defaultKeyValue = enumValue.key;
              }
              // options.push({id: enumValue.key, label: enumValue.value});
              options.push(<MenuItem key={enumValue.key} value={enumValue.key}>{enumValue.value}</MenuItem>);
            }
          } else {
            if (index === fieldValue) {
              defaultKeyValue = index;
            }
            // options.push({id: index, label: enumValue});
            options.push(<MenuItem key={index} value={index}>{enumValue}</MenuItem>);
          }
        }
      } else {
        options = fieldType.valueMap.map(value => {
          // return {id: value.key, label: value.value};
          return <MenuItem key={value.key} value={value.key}>{value.value}</MenuItem>
        });
      }
    }
    return <InputDropDown
      key={shortid()}
      ref={fieldRef}
      size={this.size}
      _id={id}
      labelElement={fieldLabel}
      defaultValue={defaultKeyValue}
      constrains={constrains}
      onErrorChange={this.onErrorChange}
      onChange={(e, id, value) => this.onChange(e, id, value)}
      disabled={BaseFieldGenerator.generateState(privilege)}>
      {options}
    </InputDropDown>;
  }

  initResourceField(id, fieldRef, fieldValue, fieldLabel, dtoProperties, privilege, constrains) {
    return <InputUpload
      _id={id}
      ref={fieldRef}
      key={shortid()}
      size={this.size}
      dtoProperties={dtoProperties}
      labelElement={fieldLabel}
      defaultValue={fieldValue}
      constrains={constrains}
      onErrorChange={this.onErrorChange}
      onChange={(e, id, value) => this.onChange(e, id, value)}
      disabled={BaseFieldGenerator.generateState(privilege)}/>;
  }
}

export default InputFieldGenerator;
